// Pure text utilities used across SW, sidepanel, and content script. Anything
// here should be deterministic and easy to test — no DOM, no chrome.* APIs,
// no async.

/**
 * Format a duration in seconds as `HH:MM:SS`. Negative or NaN inputs clamp to 0.
 */
export function formatHHMMSS(seconds: number): string {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => n.toString().padStart(2, '0')).join(':');
}

/**
 * Derive a short chat title from the first user question: collapse whitespace,
 * trim, and cap at ~40 chars (on a word boundary when possible) with an ellipsis.
 * Falls back to "New chat" for empty input.
 */
export function deriveChatTitle(question: string): string {
  const clean = question.replace(/\s+/g, ' ').trim();
  if (!clean) return 'New chat';
  const MAX = 40;
  if (clean.length <= MAX) return clean;
  const cut = clean.slice(0, MAX);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > 20 ? cut.slice(0, lastSpace) : cut;
  return `${base.trimEnd()}…`;
}

/**
 * Parse a clock string into total seconds. Accepts `HH:MM:SS`, `MM:SS`, or `SS`.
 * Returns 0 on malformed input rather than throwing — UI callers prefer
 * "no-op" over "error toast" for degenerate timestamps from the LLM.
 */
export function parseTimestamp(s: string): number {
  if (!s) return 0;
  const parts = s.split(':').map(Number);
  if (parts.some((p) => Number.isNaN(p))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] ?? 0;
}

/**
 * URL-safe kebab-case slug, ASCII-only. Strips non-alphanumeric chars, lowercases,
 * collapses whitespace into single hyphens.
 */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/**
 * Make URL-derived video filenames readable: percent-decode and strip common
 * media extensions. e.g. `Cash%20Dividends.mp4` → `Cash Dividends`.
 */
export function prettyVideoTitle(url: string): string {
  try {
    const last = decodeURIComponent(url.split('?')[0].split('/').pop() ?? 'video');
    return last.replace(/\.(mp4|webm|m3u8|mov|mkv|vtt)$/i, '');
  } catch {
    return 'video';
  }
}

/**
 * Pull series / chapter number / chapter title hints out of a CeriFi-style
 * page title like `SIE - Chapter 1 - Equity Securities`. Falls back to
 * gradually weaker patterns; returns the cleaned title as `chapterTitle`
 * when no chapter pattern matches.
 */
export interface TitleHints {
  series?: string;
  chapterNum?: number;
  chapterTitle?: string;
}

export function parseTitleHints(title: string): TitleHints {
  if (!title) return {};
  const cleaned = title.replace(/\s*[-–—|]\s*CeriFi.*$/i, '').trim();

  const m = cleaned.match(
    /^([A-Za-z0-9]+)\s*[-–—:|]\s*Chapter\s*(\d+)\s*[-–—:|]\s*(.+?)\s*$/i
  );
  if (m) {
    return {
      series: m[1],
      chapterNum: parseInt(m[2], 10),
      chapterTitle: m[3].trim(),
    };
  }

  const m2 = cleaned.match(/^Chapter\s*(\d+)\s*[-–—:|]\s*(.+?)\s*$/i);
  if (m2) {
    return { chapterNum: parseInt(m2[1], 10), chapterTitle: m2[2].trim() };
  }

  return { chapterTitle: cleaned };
}

/**
 * Render the parsed hints back into a UI-ready chapter title. Used to seed
 * the "Save as new chapter" form so Tish doesn't have to retype.
 */
export function formatChapterSuggestion(hints: TitleHints, fallback: string): string {
  if (hints.chapterNum !== undefined && hints.chapterTitle) {
    return `Chapter ${hints.chapterNum}: ${hints.chapterTitle}`;
  }
  return hints.chapterTitle || fallback;
}

/**
 * Downgrade assistant-emitted `[label](chapter:<id>)` citation links to their
 * plain label. Used on import-replace: chats are preserved but every chapter
 * id is regenerated, so the links would all be dead. Other links untouched.
 * Mirrored server-side as a regexp_replace in internal/library.
 */
export function stripChapterLinks(md: string): string {
  return md.replace(/\[([^\]]*)\]\(chapter:[^)]*\)/g, '$1');
}

/**
 * Format a chat-message timestamp (ms since epoch) for display in the
 * transcript. Same-day messages show just the time; older ones prepend a
 * short date so a long-running thread reads unambiguously. `now` is injected
 * for testability and defaults to the wall clock.
 */
export function formatMessageTimestamp(ts: number, now: number = Date.now()): string {
  const d = new Date(ts);
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (new Date(now).toDateString() === d.toDateString()) {
    return time;
  }
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${date}, ${time}`;
}
